import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Tabs, Tab, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';

interface Appointment {
  id: number;
  appointment_date: string;
  appointment_time_start: string;
  status: string;
  appointment_type: string;
  reason_for_visit: string;
  pet_name: string;
  species: string;
  vet_name: string;
}
interface Pet { id: number; name: string; species: string }
interface Vet { id: number; full_name: string }
interface Service { id: number; name: string; duration_minutes: number }
interface ApptType { id: number; name: string }

const statusColor = (s: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  if (s === 'Completed') return 'success';
  if (s === 'Confirmed' || s === 'Scheduled') return 'info';
  if (s === 'Cancelled') return 'error';
  return 'default';
};

const PortalAppointmentsPage = () => {
  const [tab, setTab] = useState(0);
  const [upcoming, setUpcoming] = useState<Appointment[]>([]);
  const [past, setPast] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  // Booking dialog
  const [bookOpen, setBookOpen] = useState(false);
  const [pets, setPets] = useState<Pet[]>([]);
  const [vets, setVets] = useState<Vet[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [apptTypes, setApptTypes] = useState<ApptType[]>([]);
  const [bPetId, setBPetId] = useState('');
  const [bVetId, setBVetId] = useState('');
  const [bServiceId, setBServiceId] = useState('');
  const [bTypeId, setBTypeId] = useState('');
  const [bDate, setBDate] = useState('');
  const [bTime, setBTime] = useState('09:00');
  const [bReason, setBReason] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      portalApi.get<{ data: Appointment[] }>(`${API_ENDPOINTS.PORTAL.APPOINTMENTS}?status=upcoming`),
      portalApi.get<{ data: Appointment[] }>(`${API_ENDPOINTS.PORTAL.APPOINTMENTS}?status=past`),
    ]).then(([upRes, pastRes]) => {
      setUpcoming(upRes.data ?? []);
      setPast(pastRes.data ?? []);
    }).catch(() => setError('Failed to load appointments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openBook = async () => {
    setBookOpen(true); setBookError(''); setBPetId(''); setBVetId(''); setBServiceId(''); setBTypeId(''); setBDate(''); setBReason('');
    try {
      const [petsRes, vetsRes, svcRes, typesRes] = await Promise.all([
        portalApi.get<{ data: Pet[] }>(API_ENDPOINTS.PORTAL.PETS),
        portalApi.get<{ data: Vet[] }>(API_ENDPOINTS.PORTAL.REF_VETS),
        portalApi.get<{ data: Service[] }>(API_ENDPOINTS.PORTAL.REF_SERVICES),
        portalApi.get<{ data: ApptType[] }>(API_ENDPOINTS.PORTAL.REF_APPOINTMENT_TYPES),
      ]);
      setPets(petsRes.data ?? []);
      setVets(vetsRes.data ?? []);
      setServices(svcRes.data ?? []);
      setApptTypes(typesRes.data ?? []);
    } catch { setBookError('Failed to load booking options'); }
  };

  const handleBook = async () => {
    if (!bPetId || !bDate || !bTime) { setBookError('Pet, date and time are required'); return; }
    setBooking(true); setBookError('');
    try {
      await portalApi.post(API_ENDPOINTS.PORTAL.BOOK_APPOINTMENT, {
        pet_id: parseInt(bPetId),
        veterinarian_id: bVetId ? parseInt(bVetId) : undefined,
        service_id: bServiceId ? parseInt(bServiceId) : undefined,
        appointment_type_id: bTypeId ? parseInt(bTypeId) : undefined,
        appointment_date: bDate,
        appointment_time_start: bTime,
        reason_for_visit: bReason || undefined,
      });
      setBookOpen(false);
      load();
    } catch (e: unknown) {
      setBookError(e instanceof Error ? e.message : 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  const handleCancel = async (id: number) => {
    if (!confirm('Cancel this appointment?')) return;
    setCancellingId(id);
    try {
      await portalApi.patch(API_ENDPOINTS.PORTAL.CANCEL_APPOINTMENT(id), {});
      load();
    } catch { /* ignore */ }
    finally { setCancellingId(null); }
  };

  const renderTable = (appts: Appointment[], emptyMsg: string, showCancel = false) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Pet</TableCell>
            <TableCell>Vet</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            {showCancel && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {appts.map(a => (
            <TableRow key={a.id} hover>
              <TableCell>{a.appointment_date} {a.appointment_time_start}</TableCell>
              <TableCell><strong>{a.pet_name}</strong><br /><Typography variant="caption" color="textSecondary">{a.species}</Typography></TableCell>
              <TableCell>{a.vet_name || '-'}</TableCell>
              <TableCell>{a.appointment_type || '-'}</TableCell>
              <TableCell>{a.reason_for_visit || '-'}</TableCell>
              <TableCell><Chip label={a.status} size="small" color={statusColor(a.status)} /></TableCell>
              {showCancel && (
                <TableCell>
                  {(a.status === 'Scheduled' || a.status === 'Confirmed') && (
                    <Button size="small" color="error" variant="outlined"
                      disabled={cancellingId === a.id}
                      onClick={() => handleCancel(a.id)}>
                      Cancel
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {appts.length === 0 && (
            <TableRow><TableCell colSpan={showCancel ? 7 : 6} align="center">{emptyMsg}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>My Appointments</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openBook}>Book Appointment</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Upcoming (${upcoming.length})`} />
        <Tab label={`Past (${past.length})`} />
      </Tabs>

      {tab === 0 && renderTable(upcoming, 'No upcoming appointments', true)}
      {tab === 1 && renderTable(past, 'No past appointments found')}

      {/* Book Appointment Dialog */}
      <Dialog open={bookOpen} onClose={() => setBookOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book an Appointment</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Pet *</InputLabel>
                <Select value={bPetId} label="Pet *" onChange={e => setBPetId(e.target.value)}>
                  {pets.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name} ({p.species})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Date *" type="date"
                value={bDate} onChange={e => setBDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Preferred Time *" type="time"
                value={bTime} onChange={e => setBTime(e.target.value)} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Veterinarian (optional)</InputLabel>
                <Select value={bVetId} label="Veterinarian (optional)" onChange={e => setBVetId(e.target.value)}>
                  <MenuItem value="">No preference</MenuItem>
                  {vets.map(v => <MenuItem key={v.id} value={String(v.id)}>{v.full_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Service (optional)</InputLabel>
                <Select value={bServiceId} label="Service (optional)" onChange={e => setBServiceId(e.target.value)}>
                  <MenuItem value="">Not specified</MenuItem>
                  {services.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Appointment Type (optional)</InputLabel>
                <Select value={bTypeId} label="Appointment Type (optional)" onChange={e => setBTypeId(e.target.value)}>
                  <MenuItem value="">Not specified</MenuItem>
                  {apptTypes.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason for visit" multiline rows={2}
                value={bReason} onChange={e => setBReason(e.target.value)} />
            </Grid>
          </Grid>
          {bookError && <Alert severity="error" sx={{ mt: 1 }}>{bookError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBook} disabled={booking || !bPetId || !bDate}>
            {booking ? <CircularProgress size={20} /> : 'Book'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalAppointmentsPage;
